import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { AuthenticatedMe, LoginInput, RegisterInput } from '@creativitas/shared';
import { fetchAvailability, fetchSession, login, logout, register } from './api';
import { pendingReturnTo } from '@/lib/returnTo';

export const SESSION_QUERY_KEY = ['session'] as const;
export const AVAILABILITY_QUERY_KEY = ['session', 'availability'] as const;

/**
 * Single source of truth for "who is signed in" on the client.
 *
 * The server stays authoritative — this only decides what to render (§64).
 */
export function useSession() {
  const query = useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    // An anonymous visitor is a valid answer, not an error worth retrying.
    retry: false,
    staleTime: 60_000,
  });

  const session: AuthenticatedMe | null = query.data ?? null;

  return {
    session,
    user: session?.user ?? null,
    access: session?.access ?? null,
    isAdmin: session?.user.role === 'admin',
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  };
}

/** Content counts for the dashboard summary (§16). */
export function useAvailability(enabled = true) {
  return useQuery({
    queryKey: AVAILABILITY_QUERY_KEY,
    queryFn: fetchAvailability,
    enabled,
  });
}

export function useLogin() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: LoginInput) => login(input),
    onSuccess: (data) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, data);
      void queryClient.invalidateQueries({ queryKey: AVAILABILITY_QUERY_KEY });
      // Пришёл по платёжной ссылке — возвращаем на оплату, иначе на главную.
      void navigate(pendingReturnTo() ?? '/dashboard', { replace: true });
    },
  });
}

export function useRegister() {
  const navigate = useNavigate();

  return useMutation({
    mutationFn: (input: RegisterInput) => register(input),
    onSuccess: (data, input) => {
      void navigate('/login', {
        replace: true,
        state: {
          registered: true,
          email: input.email,
          /* Что именно сказать на входе, решает сервер, а не догадка экрана. */
          awaitingEmailVerification: data.awaitingEmailVerification,
          verificationMailSent: data.verificationMailSent,
        },
      });
    },
  });
}

export function useLogout() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  return useMutation({
    mutationFn: logout,
    onSuccess: () => {
      // Drop every cached answer — the next user must not see this one's data.
      queryClient.clear();
      void navigate('/login', { replace: true });
    },
  });
}
